import React, { useCallback, useEffect, useRef, useState } from 'react';
import { View, Text, TouchableOpacity, TextInput, Pressable, Platform, Alert } from 'react-native';
import DefaultScreen from '@components/Global/DefaultScreenView';
import { AntDesign, Entypo, Foundation, MaterialCommunityIcons } from '@expo/vector-icons';
import WebView from 'react-native-webview';
import { useQuery } from '@tanstack/react-query';
import apis from '@api/api';
import { googleAuths } from '@reduxLocal/selectors/userSelectors';
import { SHOW_AUTH_MODAL } from '@reduxLocal/actions/types';
import { useDispatch } from 'react-redux';
import * as Animatable from 'react-native-animatable';
import LottieView from '@utils/lottieAnimation';
import SwipeableRobot from '@components/RobotHelper/SwipeableRobot';
import { Stepper, Step, StepLabel } from '@mui/material';
import GenerateSchemaForm from '@hooks/generateSchemaForm';
import { AI_API_URL } from '@api/constant';
import MaterialIcons from '@expo/vector-icons/MaterialIcons';
import { Input, Adapt, Button, Dialog, Sheet, XStack, YStack } from 'tamagui';
import isValidEmail from '@utils/checkValidEmail';

const stages = [
  { title: "Select a dataset", description: "Select a customer based dataset" },
  { title: "Select a customer", description: "Get enriched data from a customer" },
  { title: "Preview content", description: "Preview content" },
];

const PreviewEmailTemplate = ({ navigation, route }) => {
  const id = route.params.templateId
  const emailViewRef = useRef<WebView>(null);
  const [language, setLanguage] = useState('en');
  const googleAuth = googleAuths();
  const [toText, setToText] = useState('');
  const dispatch = useDispatch();
  const [aiPreview, setAiPreview] = useState(false);
  const [hideAi, setHideAi] = useState(false);
  const [aiStep, setAiStep] = useState(0);
  const [selectedCRM, setSelectedCRM] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiResponse, setAiResponse] = useState(null);
  const [mailContent, setMailContent] = useState(null);
  const toTextRef = useRef("");

  const { status, data: emailTemplate, error, isFetching, isLoading, refetch, isSuccess } = useQuery({
    queryKey: [`emailTemplate${id}`],
    queryFn: async () => {
      const data = await apis.getEmailTemplate(id)
        .then(res => {
          setAiInfo({ ...aiInfo, email_body: res.content?.html || res.content });
          setMailContent(res.content?.html || res.content);
          return res
        });
      return data
    },
    refetchOnWindowFocus: true,
  })

  const [aiInfo, setAiInfo] = useState({
    email_body: emailTemplate?.content?.html || emailTemplate?.content,
    product_description: "",
    target_customer: {}
  });

  const changeLanguage = (lang) => {
    setLanguage(lang);
  }

  const _handleGoBack = () => {
    if (Platform.OS === 'web') {
      window.history.back()
    } else {
      navigation.goBack()
    }
  }
  const _handleZoom = useCallback(() => {
    emailViewRef.current?.props.zoomIn()
  }, [])

  const _handleTest = async (content, subject) => {
    // const to = toTextRef.current!.state.value || toTextRef.current!.state.value;

    console.log(toTextRef.current.valueOf(), "Sending to", content, subject);

    if(isValidEmail(toTextRef.current.valueOf())){
      await apis.sendTestEmail(content, toTextRef.current.valueOf(), subject)
    } else {
      Alert.alert("Invalid email address", "Please enter a valid email address")
    }
  };

  const _handleEdit = () => {
    navigation.navigate('CreateEmailTemplate', { templateId: id });
  }


  const zoomIn = () => {
    if (emailViewRef.current) {
      emailViewRef.current.injectJavaScript(`
        const meta = document.querySelector('meta[name="viewport"]');
        if (meta) {
          meta.setAttribute('content', 'width=device-width, initial-scale=1.0, maximum-scale=3.0, user-scalable=1');
        }
      `);
    }
  };

  //Extract text body from html content as only text is needed for AI and set to aiState
  const extractText = (html) => {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const text = doc.body.textContent || "";
    setAiInfo({ ...aiInfo, email_body: text });
  }


  const zoomOut = () => {
    if (emailViewRef.current) {
      emailViewRef.current!.injectJavaScript(`
        const meta = document.querySelector('meta[name="viewport"]');
        if (meta) {
          meta.setAttribute('content', 'width=device-width, initial-scale=0.5, maximum-scale=1.0, user-scalable=1');
        }
      `);
    }
  };


  const onLoadWebView = () => {
    // if (emailViewRef.current) {
    //   emailViewRef.current!.injectJavaScript(`
    //       const meta = document.createElement('meta');
    //       meta.setAttribute('content', 'width=device-width, initial-scale=0.5, maximum-scale=0.5, user-scalable=0');
    //       meta.setAttribute('name', 'viewport');
    //       document.getElementsByTagName('head')[0].appendChild(meta);

    //       var links = document.getElementsByTagName('a');
    //       for (var i = 0; i < links.length; i++) {
    //         links[i].removeAttribute('href');
    //       }
    //     `);
    //   emailViewRef.current!.reload();
    // }
  };

  return (
    <DefaultScreen scrollDisabled backButton noMargin
      style={{ paddingBottom: 50, flex: 1 }}
      searchBar={{
        otherComponents: [
          { functionName: 'zoomOut', isIcon: true, function: zoomOut, icon: (<View style={{ flex: 1, borderRadius: 30 }}><Foundation name="zoom-out" size={24} top={10} color="black" /></View>) },
          { functionName: 'zoomIn', isIcon: true, function: zoomIn, icon: (<View style={{ flex: 1, borderRadius: 30 }}><Foundation name="zoom-in" size={24} top={10} color="black" /></View>) },
          { functionName: 'edit', function: _handleEdit, isIcon: true, icon: <Entypo name="edit" size={24} color="black" /> },
          {
            functionName: "sendTest", function: async () => { }, isIcon: true, icon:
              <Dialog key={"send-test-email"} >
                <Dialog.Trigger asChild style={{ top: 16, minHeight: 60 }}>
                  <MaterialIcons name="send-time-extension" size={24} color="black" />
                </Dialog.Trigger>
                <Adapt platform="touch">
                  <Sheet animation="medium" zIndex={200000} modal >
                    <Sheet.Frame padding="$4" gap="$4">
                      <Adapt.Contents />
                    </Sheet.Frame>
                    <Sheet.Overlay
                      style={{ maxHeight: "80%" }}
                      animation="lazy"
                      enterStyle={{ opacity: 0 }}
                      exitStyle={{ opacity: 0 }}
                    />
                  </Sheet>
                </Adapt>
                <Dialog.Portal>
                  <Dialog.Overlay
                    animation="quick"
                    opacity={0.5}
                    style={{ zIndex: 1000, padding: 50 }}
                    enterStyle={{ opacity: 0 }}
                    exitStyle={{ opacity: 0 }}
                  />
                  <Dialog.Content bordered
                    key={"dialog-content-test-email"}
                    elevate
                    animation={[
                      'quick',
                      {
                        opacity: {
                          overshootClamping: true,
                        },
                      },
                    ]}
                    enterStyle={{ x: 0, y: -20, opacity: 0, scale: 0.9 }}
                    exitStyle={{ x: 0, y: 10, opacity: 0, scale: 0.95 }}
                    x={0}
                    opacity={1}
                    y={0}>
                    <YStack flex={1} gap={10} maxHeight={"80%"}>
                      <Dialog.Title>Send Test Email</Dialog.Title>
                      <Dialog.Description>Send a test email to preview how it will look</Dialog.Description>
                      <YStack style={{ flex: 1, gap: 40 }}>
                        <Input
                          onChangeText={(text) => toTextRef.current = text}
                          style={{ fontWeight: "bold", fontSize: 24, borderBottomWidth: 1, padding: 10, minWidth: 300, flex: 1, maxHeight: 80 }}
                          placeholder="Send test email to..."
                        />
                        <XStack gap={20} maxHeight={100} flex={1}>
                          <Dialog.Close key="close-dialog" displayWhenAdapted asChild>
                            <Button flex={1}>Cancel</Button>
                          </Dialog.Close>
                          <Dialog.Close key="send-email" displayWhenAdapted asChild>
                            <Button flex={1} onPress={async () => {
                              console.log("send test email",)
                              await _handleTest(emailTemplate._id, "TEST")
                            }}>
                              Send
                              <MaterialCommunityIcons
                                adjustsFontSizeToFit
                                allowFontScaling
                                name={'send'}
                                size={24}
                                color={"rgb(255, 255, 255)"}
                              />
                            </Button>
                          </Dialog.Close>
                        </XStack>
                      </YStack>
                    </YStack>
                  </Dialog.Content>
                </Dialog.Portal>
              </Dialog>
          },
          {
            functionName: "aiPreview", isIcon: true, function: () => { setAiPreview(!aiPreview) }, icon: <Pressable onPress={() => setAiPreview(!aiPreview)} style={{ flex: 1, borderRadius: 20, alignSelf: "flex-end", maxHeight: 25 }}>
              {({ hovered }) => (
                <Animatable.View useNativeDriver style={{ flexDirection: "row", flex: 1, borderRadius: 20, backgroundColor: hovered ? "rgb(90,192,234)" : (aiPreview ? "rgb(90,192,234)" : "transparent"), alignSelf: "flex-end" }}>
                  <MaterialCommunityIcons
                    adjustsFontSizeToFit
                    allowFontScaling
                    name={'brain'}
                    style={{ flex: 1, }}
                    size={24}
                    color={"rgb(90,192,234)"}
                  />
                  <LottieView source={require('@assets/animations/automation_robot.json')} autoPlay loop style={{ width: 25, height: 25 }} />
                </Animatable.View>
              )}
            </Pressable>
          },
        ]
      }}>
      {
        isFetching ?
          <View style={{ flexGrow: 10, justifyContent: "center", alignContent: "center", alignItems: "center", flex: 1 }}>
            <LottieView source={require("@assets/animations/spin-leaf-loader.json")} autoPlay loop style={{ flex: 1, alignSelf: 'center', minHeight: 150, maxHeight: 150, maxWidth: 150, minWidth: 150 }} />
            <Text style={{ textAlign: "center" }}>Loading...</Text>
          </View>
          :
          <View style={{ flex: Platform.select({ web: undefined, default: 1 }), minHeight: Platform.select({ web: "100%" }), padding: 20 }}>
            <View style={{ flex: 6, borderRadius: 10, flexDirection: "row", overflow: 'hidden', alignSelf: 'stretch', minHeight: "90%", minWidth: Platform.select({ default: "113%", web: "100%" }) }}>
              {
                mailContent && isSuccess ?
                <Animatable.View transition={'flex'} style={{ flex: aiPreview ? 4 : 7 }} onAnimationEnd={() => {
                  if (!hideAi) {
                    setHideAi(false)
                  }
                  else {
                    setHideAi(true)
                  }
                }}>
                    <WebView textZoom={10} style={{ flex: 4, borderRadius: 15 }} containerStyle={{ backgroundColor: "white", borderRadius: 15 }} ref={emailViewRef} onLoad={onLoadWebView} allowsLinkPreview={false} scalesPageToFit source={{ html: mailContent }} />
                </Animatable.View>
                :
                <View style={{ flex: 1, alignItems: 'center', justifyContent: 'center', padding: 40 }}>
                  <Entypo name="pencil" size={48} color="#bbb" style={{ marginBottom: 16 }} />
                  <Text style={{ fontSize: 18, color: '#888', textAlign: 'center' }}>
                    No content yet saved in this template, build some using <Text style={{ fontWeight: 'bold' }}>pencil</Text>.
                  </Text>
                </View>
              }
              {
                !hideAi &&
                <Animatable.View transition={['flex', 'opacity']} style={{ margin: 20, flex: aiPreview ? 1.8 : 0.0001, opacity: aiPreview ? 1 : 0, pointerEvents: aiPreview ? undefined : 'none' }}>
                  <SwipeableRobot removeSwipe textHelp={["I can help you visualise your email using AI"]} style={{ flex: 1, maxHeight: 180, marginBottom: 20 }} />
                  <View style={{ flex: 3, flexGrow: 10, paddingVertical: 20 }}>
                    <GenerateSchemaForm
                      retainInitialValues={selectedCRM ? true : false}
                      initialData={selectedCRM}
                      submitFunction={(vals) => {
                        console.log(vals)
                        setSelectedCRM(vals)
                        setAiStep(aiStep + 1);
                      }} buttonLabel='Next' schema={[{ type: 'string', title: "select_dataset", validation: { required: true }, ref: 'customer_datas', helpText: "The name of the stage" }]} />
                    {
                      selectedCRM && __DEV__ &&
                      <Text style={{ textAlign: "center" }}>{selectedCRM.select_dataset[0]?._id}</Text>
                    }
                    {
                      aiStep > 0 && selectedCRM &&
                      <GenerateSchemaForm initialData={selectedCRM} otherVals={selectedCRM?.select_dataset[0]} submitFunction={async (vals) => {
                        setAiLoading(true);
                        console.log(vals)
                        setAiInfo({ ...aiInfo, target_customer: vals.select_dataset.data });
                        extractText(emailTemplate.content);
                        setAiStep(aiStep + 1);
                        await fetch(`${AI_API_URL}/preview-ai-email`, {
                          method: 'POST',
                          headers: {
                            'Content-Type': 'application/json',
                          },
                          body: JSON.stringify({
                            email_body: aiInfo.email_body,
                            product_description: aiInfo.product_description,
                            target_customer: aiInfo.target_customer,
                          }),
                        })
                          .then((res) => res.json())  // Parse the JSON from the response
                          .then((data) => {
                            console.log('final response', data);
                            setAiResponse("How about this?")
                            //Replace only the contents of the email body with the AI generated content
                            const doc = new DOMParser().parseFromString(emailTemplate.content, 'text/html');
                            // Find the div with id 'ieykzj' and replace its content
                            const targetDiv = doc.getElementById('ieykzj');
                            if (targetDiv) {
                              doc.getElementById('ieykzj').innerHTML = data.email_body || "Could not find the right response";
                            } else {
                              // If the div is not found, optionally set a default message or handle the case
                              doc.body.innerHTML = data.email_body || "Could not find the right response";
                            }

                            // Get the updated HTML content from the document
                            const finalEmail = doc.documentElement.outerHTML;
                            setMailContent(finalEmail);
                          })
                          .catch((error) => {
                            console.error('Error fetching AI email preview:', error);
                            setAiResponse("Sorry, I could not find the right response!");  // Optionally handle errors by setting a null response
                          })
                          .finally(() => setAiLoading(false));  // Stop loading indicator
                      }} buttonLabel='Next' schema={[{ type: 'string', title: "select_dataset", validation: { required: true }, ref: 'crm_item_select', helpText: "The name of the stage" }]} />
                    }
                    {
                      aiInfo && aiStep > 1 &&
                      <>
                        {
                          aiLoading ?
                            (
                                <Animatable.View transition="flex" style={{ flex: aiStep > 1 ? 1 : 0.0001 }}>
                                <LottieView source={require("@assets/animations/spin-leaf-loader.json")} autoPlay loop style={{ flex: 1, alignSelf: 'center', maxHeight: 150, maxWidth: 150 }} />
                              </Animatable.View>
                            )
                            :
                            (
                              <Text>{JSON.stringify(aiResponse)}</Text>
                            )
                        }
                      </>
                    }
                  </View>
                    {
                      Platform.OS === 'web' &&
                  <Stepper activeStep={aiStep} style={{ bottom: 0 }}>
                    {
                      stages.map((stage, index) => {
                        return (
                          <Step key={index}>
                            <StepLabel style={{ userSelect: "none" }}>{stage.title}</StepLabel>
                          </Step>
                        )
                      })
                    }
                  </Stepper>
                    }
                </Animatable.View>
              }
            </View>
          </View>
      }
    </DefaultScreen>
  )
}
export default PreviewEmailTemplate;